import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '@htx/environment';
import {
  EmailTemplateDetail,
  EmailTemplateKey,
  EmailTemplateListItem,
  EmailTemplatePreviewResult,
  EmailTemplateUpdateDto
} from './email-template.model';

@Injectable({
  providedIn: 'root'
})
export class EmailTemplateService {
  private httpClient = inject(HttpClient);
  private readonly baseUrl = `${environment.backendUrl}/admin/email-templates`;

  private normalizeListItem(item: EmailTemplateListItem): EmailTemplateListItem {
    return {
      ...item,
      subject: item.subject ?? item.effectiveSubject ?? ''
    };
  }

  private normalizeDetail(detail: EmailTemplateDetail): EmailTemplateDetail {
    return {
      ...detail,
      subject: detail.subject ?? detail.effectiveSubject ?? '',
      bodyHtml: detail.bodyHtml ?? detail.effectiveBodyHtml ?? ''
    };
  }

  private normalizePreview(result: EmailTemplatePreviewResult): EmailTemplatePreviewResult {
    return {
      subject: result.subject ?? result.effectiveSubject ?? '',
      bodyHtml: result.bodyHtml ?? result.effectiveBodyHtml ?? ''
    };
  }

  /** GET /admin/email-templates */
  list(): Observable<EmailTemplateListItem[]> {
    return this.httpClient
      .get<EmailTemplateListItem[]>(this.baseUrl)
      .pipe(map((items) => items.map((item) => this.normalizeListItem(item))));
  }

  /** GET /admin/email-templates/{key} */
  get(key: EmailTemplateKey): Observable<EmailTemplateDetail> {
    return this.httpClient
      .get<EmailTemplateDetail>(`${this.baseUrl}/${key}`)
      .pipe(map((detail) => this.normalizeDetail(detail)));
  }

  /** PUT /admin/email-templates/{key} */
  update(key: EmailTemplateKey, dto: EmailTemplateUpdateDto): Observable<EmailTemplateDetail> {
    return this.httpClient
      .put<EmailTemplateDetail>(`${this.baseUrl}/${key}`, dto)
      .pipe(map((detail) => this.normalizeDetail(detail)));
  }

  /** POST /admin/email-templates/{key}/reset */
  reset(key: EmailTemplateKey): Observable<EmailTemplateDetail> {
    return this.httpClient
      .post<EmailTemplateDetail>(`${this.baseUrl}/${key}/reset`, {})
      .pipe(map((detail) => this.normalizeDetail(detail)));
  }

  /** POST /admin/email-templates/{key}/preview */
  preview(
    key: EmailTemplateKey,
    dto: EmailTemplateUpdateDto
  ): Observable<EmailTemplatePreviewResult> {
    return this.httpClient
      .post<EmailTemplatePreviewResult>(`${this.baseUrl}/${key}/preview`, dto)
      .pipe(map((result) => this.normalizePreview(result)));
  }
}
